import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Typography,
} from "@mui/material";
import { service } from "../../model/service";
import ModalError from "../Generics/ModalError";

const AmlDocument = ({
  caseData,
  languange,
  uid,
  documents,
  refreshDocuments,
}) => {
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [signedFile, setSignedFile] = useState(null);
  const [sizeError, setSizeError] = useState(null);
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    description: "",
  });

  const uploadRef = useRef(null);
  const t = (ita, eng) => (languange === "ENG" ? eng : ita);

  const showError = (title, description) =>
    setErrorModal({ open: true, title, description });
  const closeError = () =>
    setErrorModal({ open: false, title: "", description: "" });

  const existingSignedDoc = (documents || []).find(
    (d) => d.type === "client_aml_signed",
  );

  useEffect(() => {
    if (existingSignedDoc) {
      setSignedFile({
        name: existingSignedDoc.original_name,
        id: existingSignedDoc.id,
      });
    }
  }, [existingSignedDoc?.id]);

  // Scarica il generated_pdf già esistente — la generazione avviene al passaggio step5→6
  const handleDownload = async () => {
    try {
      setDownloading(true);
      const generatedDoc = (documents || []).find(
        (d) => d.type === "generated_pdf",
      );

      console.log(generatedDoc);
      if (!generatedDoc?.id) {
        showError(
          t("Documento non trovato", "Document not found"),
          t(
            "Il documento PDF non è ancora disponibile. Riprova.",
            "The PDF document is not available yet. Please try again.",
          ),
        );
        return;
      }
      const blob = await service.downloadAmlDocument(generatedDoc.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `aml_${generatedDoc.aml_case_id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showError(
        t("Errore download", "Download error"),
        t(
          "Si è verificato un errore durante il download del documento. Riprova.",
          "An error occurred while downloading the document. Please try again.",
        ),
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setSizeError(t("File supera la dimensione massima consentita di 4,5 MB", "File exceeds the maximum allowed size of 4.5 MB"));
      if (uploadRef.current) uploadRef.current.value = "";
      return;
    }
    setSizeError(null);
    try {
      setUploading(true);
      setSignedFile(file);
      const formData = new FormData();
      formData.append("file", file);
      await service.uploadAmlSigned(formData);
      if (typeof refreshDocuments === "function") await refreshDocuments();
    } catch (err) {
      setSignedFile(null);
      showError(
        t("Errore caricamento", "Upload error"),
        t(
          "Si è verificato un errore durante il caricamento del documento firmato. Riprova.",
          "An error occurred while uploading the signed document. Please try again.",
        ),
      );
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveSigned = async () => {
    try {
      setRemoving(true);
      if (signedFile?.id) {
        await service.deleteDocument(signedFile.id);
        if (typeof refreshDocuments === "function") await refreshDocuments();
      }
      setSignedFile(null);
      if (uploadRef.current) uploadRef.current.value = "";
    } catch (err) {
      showError(
        t("Errore eliminazione", "Deletion error"),
        t(
          "Si è verificato un errore durante l'eliminazione del documento. Riprova.",
          "An error occurred while deleting the document. Please try again.",
        ),
      );
    } finally {
      setRemoving(false);
    }
  };

  if (downloading) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 2000,
        }}
      >
        <CircularProgress sx={{ color: "#1b2838" }} />
      </div>
    );
  }

  return (
    <>
      <Grid container style={{ display: "flex", justifyContent: "center" }}>
        <Grid item xs={12} md={10}>
          <Box sx={{ mb: 3 }}>
            <Typography
              className="color-blue-dark"
              style={{
                fontSize: "24px",
                fontWeight: 700,
                letterSpacing: "-0.02em",
              }}
            >
              {t("Firma e invio del modulo", "Form signing and submission")}
            </Typography>
          </Box>

          <Typography
            className="color-blue-dark"
            sx={{ fontSize: "15px", mb: 4, color: "#666" }}
          >
            {t(
              "Il tuo modulo anti-riciclaggio è stato generato. Segui le istruzioni dei box sottostanti.",
              "Your anti-money laundering form has been created. Follow the instructions in the boxes.",
            )}
          </Typography>

          {/* ── Download ── */}
          <Box
            sx={{
              mb: 2,
              borderRadius: "12px",
              border: "1px solid #e8e8e8",
              overflow: "hidden",
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 3,
                py: 1.5,
                backgroundColor: "#f9f9f9",
                borderBottom: "1px solid #e8e8e8",
              }}
            >
              <Typography
                style={{
                  fontWeight: 700,
                  fontSize: "16px",
                  letterSpacing: "0.06em",
                  color: "#1b2838",
                }}
              >
                {t(
                  "1. Scarica e firma il modulo",
                  "1. Download and sign the form",
                )}
              </Typography>
            </Box>
            <Box sx={{ px: 3, py: 2 }}>
              <Typography sx={{ fontSize: "13px", color: "#888", mb: 2 }}>
                {t(
                  "Puoi stampare il PDF e firmarlo a penna oppure applicare direttamente sul file una firma digitale in formato PADES.",
                  "You may print the PDF and sign it manually or apply a digital signature in PADES format.",
                )}
              </Typography>
              <Button
                onClick={handleDownload}
                className="button-bg-empty"
                sx={{
                  height: "40px",
                  width: "240px",
                  minWidth: "0 !important",
                  boxSizing: "border-box",
                  border: "1px solid #1b2838 !important",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  px: "16px",
                }}
              >
                <div
                  className="icon-button-download"
                  style={{ flexShrink: 0 }}
                />
                <Typography
                  className="color-blue-dark"
                  sx={{
                    textTransform: "initial",
                    fontSize: "15px",
                    fontWeight: 500,
                    mt: "3px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("Scarica PDF", "Download PDF")}
                </Typography>
              </Button>
            </Box>
          </Box>

          {/* ── Upload ── */}
          <Box
            sx={{
              mb: 2,
              borderRadius: "12px",
              border: "1px solid #e8e8e8",
              overflow: "hidden",
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 3,
                py: 1.5,
                backgroundColor: "#f9f9f9",
                borderBottom: "1px solid #e8e8e8",
              }}
            >
              <Typography
                style={{
                  fontWeight: 700,
                  fontSize: "16px",
                  letterSpacing: "0.06em",
                  color: "#1b2838",
                }}
              >
                {t(
                  "2. Carica il modulo firmato",
                  "2. Upload the anti-money laundering form",
                )}
              </Typography>
            </Box>
            <Box sx={{ px: 3, py: 2 }}>
              <Typography sx={{ fontSize: "13px", color: "#888", mb: 1 }}>
                {t(
                  "Una volta caricato il modulo, puoi procedere all’invio.",
                  "Once the form is uploaded, you can submit it.",
                )}
              </Typography>
              {sizeError ? (
                <Typography style={{ fontSize: "12px", color: "#bc2957", marginBottom: "8px" }}>
                  {sizeError}
                </Typography>
              ) : (
                <Typography style={{ fontSize: "12px", color: "#1b2838", fontWeight: 700, marginBottom: "8px" }}>
                  {t("Dimensione massima del file 4,5 MB", "Maximum file size 4.5 MB")}
                </Typography>
              )}

              {signedFile ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "0 14px",
                    height: "40px",
                    width: "240px",
                    boxSizing: "border-box",
                    flexShrink: 0,
                    borderRadius: "8px",
                    border: "1px solid #1b2838",
                    opacity: uploading || removing ? 0.7 : 1,
                  }}
                >
                  {uploading || removing ? (
                    <CircularProgress size={16} sx={{ color: "#1b2838" }} />
                  ) : (
                    <div
                      className="icon-button-upload"
                      style={{ flexShrink: 0 }}
                    />
                  )}
                  <Typography
                    className="color-blue-dark"
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      mt: "2px",
                    }}
                  >
                    {signedFile.name}
                  </Typography>
                  {!uploading && !removing && (
                    <div
                      className="icon-remove-file"
                      style={{ flexShrink: 0, cursor: "pointer" }}
                      onClick={handleRemoveSigned}
                    />
                  )}
                </Box>
              ) : (
                <Button
                  component="label"
                  disabled={uploading}
                  className="button-bg-empty"
                  sx={{
                    height: "40px",
                    width: "240px",
                    minWidth: "0 !important",
                    boxSizing: "border-box",
                    border: "1px solid #1b2838 !important",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    px: "16px",
                  }}
                >
                  <div
                    className="icon-button-upload"
                    style={{ flexShrink: 0 }}
                  />
                  <Typography
                    className="color-blue-dark"
                    sx={{
                      textTransform: "initial",
                      fontSize: "15px",
                      fontWeight: 500,
                      mt: "3px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("Carica PDF firmato", "Upload signed PDF")}
                  </Typography>
                  <input
                    ref={uploadRef}
                    type="file"
                    accept="application/pdf"
                    hidden
                    onChange={(e) => {
                      handleUpload(e);
                      e.target.value = "";
                    }}
                  />
                </Button>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Modal open={errorModal.open} onClose={closeError}>
        <ModalError
          modalInfo={true}
          title={errorModal.title}
          descriptionOne={errorModal.description}
          copyButton={t("Chiudi", "Close")}
          confirm={closeError}
          onClose={closeError}
        />
      </Modal>
    </>
  );
};

export default AmlDocument;
